import { useMemo } from "react";
import { rgbForClicks, rgbToCss } from "../lib/colorScale";
import { textColorForBackground } from "../lib/luminance";
import { formatClicks, formatEuroFromCents } from "../lib/format";
import type { ClickCounterRow, Country } from "../lib/types";
import { countryDisplayName } from "../hooks/useCountries";
import { useLanguage } from "../i18n/LanguageContext";

export interface CountryListProps {
  countries: Map<number, Country>;
  /**
   * Combined totals - clicks is free+purchased votes added together,
   * amount_cents (when present and > 0) is the country's total EUR support.
   * See MapPage's `combinedRows`.
   */
  clicksByCountry: Map<number, ClickCounterRow>;
  maxClicks: number;
  selectedCountryNumeric: number | null;
  onSelectCountry: (countryNumeric: number, countryName: string) => void;
}

/**
 * Right-hand ranked list. Per spec: only countries with at least one vote
 * (free click or purchased click), sorted descending by the combined total,
 * rank + name + formatted count (+ EUR support, when any), no bars/extra
 * visual indicators, row background uses the exact same color function as
 * the map and text color is chosen from that background's relative
 * luminance (src/lib/luminance.ts), not a fixed click-count threshold.
 */
export function CountryList({
  countries,
  clicksByCountry,
  maxClicks,
  selectedCountryNumeric,
  onSelectCountry,
}: CountryListProps) {
  const { t, language } = useLanguage();
  const ranked = useMemo(() => {
    return Array.from(clicksByCountry.values())
      .filter((row) => row.clicks > 0)
      .sort((a, b) => b.clicks - a.clicks)
      .map((row, index) => {
        const country = countries.get(row.country_numeric);
        const rgb = rgbForClicks(row.clicks, maxClicks);
        return {
          rank: index + 1,
          countryNumeric: row.country_numeric,
          name: countryDisplayName(country, `#${row.country_numeric}`),
          clicks: row.clicks,
          amountCents: row.amount_cents,
          background: rgbToCss(rgb),
          textColor: textColorForBackground(rgb),
        };
      });
  }, [clicksByCountry, countries, maxClicks]);

  if (ranked.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-300 rounded-xl">
        {t("list.empty")}
      </div>
    );
  }

  return (
    <ol className="flex flex-col gap-1.5 overflow-y-auto max-h-[520px] pr-1" aria-label={t("list.ariaLabel")}>
      {ranked.map((entry) => {
        const isSelected = entry.countryNumeric === selectedCountryNumeric;
        return (
          <li key={entry.countryNumeric}>
            <button
              type="button"
              onClick={() => onSelectCountry(entry.countryNumeric, entry.name)}
              className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-left transition ${
                isSelected ? "ring-2 ring-offset-1 ring-slate-900" : ""
              }`}
              style={{ background: entry.background, color: entry.textColor }}
            >
              <span className="text-xs font-semibold opacity-80 w-6 shrink-0 tabular-nums">{entry.rank}.</span>
              <span className="flex-1 font-medium truncate">{entry.name}</span>
              <span className="text-sm font-semibold tabular-nums">{formatClicks(entry.clicks, language)}</span>
              {typeof entry.amountCents === "number" && entry.amountCents > 0 && (
                <span className="text-xs opacity-90 tabular-nums whitespace-nowrap">
                  {formatEuroFromCents(entry.amountCents, language)}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  );
}
